import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Slider,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

// OpenLayers imports
import Map from 'ol/Map';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import ImageSource from 'ol/source/ImageCanvas';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import { Style, Fill, Stroke } from 'ol/style';
import { fromLonLat } from 'ol/proj';

// Types and interfaces
interface GridData {
  probabilities: number[];
  coordinates: [number, number][]; // [lat, lon] pairs
  confidences?: number[]; // confidence values (0-1) for each grid point
  metadata?: {
    bounds: [number, number, number, number]; // [min_lat, min_lon, max_lat, max_lon]
    grid_size: [number, number]; // [rows, cols]
    cell_dimensions: {
      width_deg: number;
      height_deg: number;
      width_km: number;
      height_km: number;
    };
    polygon_bounds: {
      min_lat: number;
      max_lat: number;
      min_lon: number;
      max_lon: number;
      center_lat: number;
      center_lon: number;
    };
  };
}

interface GridCell {
  id: string;
  coordinate: [number, number]; // [lat, lon]
  probability: number;
  confidence?: number;
  row: number;
  col: number;
  bounds: [number, number, number, number]; // Cell bounds
}

interface GridStructure {
  isUniform: boolean;
  gridType: 'uniform' | 'adaptive' | 'irregular';
  detectedRows: number;
  detectedCols: number;
  cellSpacing: {
    avgLatSpacing: number;
    avgLonSpacing: number;
    spacingVariation: number;
  };
}

interface GridPotentialLayerProps {
  map: Map | null;
  gridData: GridData | null;
  mineralType: string;
  visible: boolean;
  onVisibilityChange: (visible: boolean) => void;
  onCellClick?: (cell: GridCell) => void;
  zoomLevel?: number;
  polygonBounds?: [number, number, number, number];
  showControlPanel?: boolean;
  threshold?: number;
  opacity?: number;
  modulated?: boolean;
}

interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Mineral-specific color schemes (matching the reference image style)
const MINERAL_COLOR_SCHEMES = {
  gold: {
    name: 'Gold Potential',
    stops: [
      { value: 0.0, color: [255, 253, 230, 0] },
      { value: 0.2, color: [255, 244, 173, 120] },
      { value: 0.4, color: [255, 233, 120, 180] },
      { value: 0.6, color: [255, 215, 72, 220] },
      { value: 0.8, color: [240, 188, 0, 245] },
      { value: 1.0, color: [187, 132, 0, 255] }
    ]
  },
  silver: {
    name: 'Silver Potential', 
    stops: [
      { value: 0.0, color: [255, 255, 255, 0] },
      { value: 0.25, color: [244, 244, 244, 120] },
      { value: 0.45, color: [224, 224, 224, 185] },
      { value: 0.65, color: [198, 198, 198, 225] },
      { value: 0.85, color: [166, 166, 166, 245] },
      { value: 1.0, color: [136, 136, 136, 255] }
    ]
  },
  copper: {
    name: 'Copper Potential',
    stops: [
      { value: 0.0, color: [255, 244, 228, 0] },
      { value: 0.25, color: [246, 210, 162, 120] },
      { value: 0.45, color: [224, 166, 112, 190] },
      { value: 0.65, color: [191, 130, 76, 230] },
      { value: 0.85, color: [146, 94, 48, 248] },
      { value: 1.0, color: [96, 62, 32, 255] }
    ]
  }
};

// Zoom thresholds for different rendering modes
const ZOOM_THRESHOLDS = {
  SMOOTH_GRADIENT: 12,  // Below: maximum smoothness
  HYBRID_MODE: 15,      // Between: balanced approach  
  DISCRETE_CELLS: 17,   // Above: clear cell boundaries
  SHOW_VALUES: 19       // Above: show probability values
};

// Grid data transformation and validation service
class GridTransformService {
  static validateGridData(data: GridData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    
    // Basic structure validation
    if (!data || !data.probabilities || !data.coordinates) {
      errors.push('Missing required grid data properties');
      return { isValid: false, errors, warnings };
    }
    
    if (!Array.isArray(data.probabilities) || !Array.isArray(data.coordinates)) {
      errors.push('Grid probabilities and coordinates must be arrays');
      return { isValid: false, errors, warnings };
    }
    
    // Length consistency check
    if (data.probabilities.length !== data.coordinates.length) {
      errors.push(`Data length mismatch: ${data.probabilities.length} probabilities vs ${data.coordinates.length} coordinates`);
    }
    
    // Value range validation
    const invalidProbs = data.probabilities.filter(p => p < 0 || p > 1 || isNaN(p));
    if (invalidProbs.length > 0) {
      errors.push(`${invalidProbs.length} probability values out of range [0,1]`);
    }
    
    // Coordinate validation
    const invalidCoords = data.coordinates.filter(coord => 
      !Array.isArray(coord) || coord.length !== 2 || 
      isNaN(coord[0]) || isNaN(coord[1]) ||
      Math.abs(coord[0]) > 90 || Math.abs(coord[1]) > 180
    );
    if (invalidCoords.length > 0) {
      warnings.push(`${invalidCoords.length} coordinates appear invalid`);
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }
  
  static detectGridStructure(coordinates: [number, number][]): GridStructure {
    if (coordinates.length < 4) {
      return {
        isUniform: false,
        gridType: 'irregular',
        detectedRows: 1,
        detectedCols: coordinates.length,
        cellSpacing: { avgLatSpacing: 0, avgLonSpacing: 0, spacingVariation: 0 }
      };
    }
    
    // Sort coordinates to detect pattern
    const sortedByLat = [...coordinates].sort((a, b) => a[0] - b[0]);
    const sortedByLon = [...coordinates].sort((a, b) => a[1] - b[1]);
    
    // Calculate unique positions (rounded to avoid floating point issues)
    const uniqueLats = Array.from(new Set(sortedByLat.map(c => Math.round(c[0] * 1000000) / 1000000)));
    const uniqueLons = Array.from(new Set(sortedByLon.map(c => Math.round(c[1] * 1000000) / 1000000)));
    
    const detectedRows = uniqueLats.length;
    const detectedCols = uniqueLons.length;
    
    // Calculate average spacing
    const latSpacings = uniqueLats.slice(1).map((lat, i) => lat - uniqueLats[i]);
    const lonSpacings = uniqueLons.slice(1).map((lon, i) => lon - uniqueLons[i]);
    
    const avgLatSpacing = latSpacings.reduce((a, b) => a + b, 0) / latSpacings.length || 0;
    const avgLonSpacing = lonSpacings.reduce((a, b) => a + b, 0) / lonSpacings.length || 0;
    
    // Calculate spacing variation (coefficient of variation)
    const latVariance = latSpacings.reduce((sum, spacing) => sum + Math.pow(spacing - avgLatSpacing, 2), 0) / latSpacings.length;
    const lonVariance = lonSpacings.reduce((sum, spacing) => sum + Math.pow(spacing - avgLonSpacing, 2), 0) / lonSpacings.length;
    const spacingVariation = Math.sqrt((latVariance + lonVariance) / 2) / Math.max(avgLatSpacing, avgLonSpacing);
    
    // Determine grid type
    const isRegularGrid = detectedRows * detectedCols === coordinates.length;
    const isUniform = spacingVariation < 0.1; // Less than 10% variation
    
    let gridType: 'uniform' | 'adaptive' | 'irregular' = 'irregular';
    if (isRegularGrid && isUniform) {
      gridType = 'uniform';
    } else if (isRegularGrid) {
      gridType = 'adaptive';
    }
    
    return {
      isUniform,
      gridType,
      detectedRows,
      detectedCols,
      cellSpacing: {
        avgLatSpacing,
        avgLonSpacing,
        spacingVariation
      }
    };
  }
  
  static transformToGridCells(data: GridData, polygonBounds?: [number, number, number, number]): GridCell[] {
    const validation = this.validateGridData(data);
    if (!validation.isValid) {
      console.error('Grid validation failed:', validation.errors);
      return [];
    }
    
    if (validation.warnings.length > 0) {
          }
    
    const structure = this.detectGridStructure(data.coordinates);
    const cells: GridCell[] = [];
    
    // Use metadata bounds if available, otherwise calculate from coordinates
    let bounds: [number, number, number, number];
    if (data.metadata?.bounds) {
      bounds = data.metadata.bounds;
    } else {
      const lats = data.coordinates.map(c => c[0]);
      const lons = data.coordinates.map(c => c[1]);
      bounds = [Math.min(...lats), Math.min(...lons), Math.max(...lats), Math.max(...lons)];
    }
    
    const cellWidth = data.metadata?.cell_dimensions?.width_deg || structure.cellSpacing.avgLonSpacing;
    const cellHeight = data.metadata?.cell_dimensions?.height_deg || structure.cellSpacing.avgLatSpacing;
    
    // Create grid cells
    data.coordinates.forEach((coord, index) => {
      const [lat, lon] = coord;
      const probability = data.probabilities[index];
      const confidence = data.confidences ? data.confidences[index] : 0.8; // Default confidence if not provided
      
      // Skip invalid data points
      if (isNaN(lat) || isNaN(lon) || isNaN(probability)) {
        return;
      }
      
      // Check if point is within polygon bounds (if provided)
      if (polygonBounds) {
        const [minLat, minLon, maxLat, maxLon] = polygonBounds;
        if (lat < minLat || lat > maxLat || lon < minLon || lon > maxLon) {
                  }
      }
      
      // Calculate row/col position for uniform grids
      let row = 0, col = 0;
      if (structure.gridType === 'uniform') {
        row = Math.round((lat - bounds[0]) / cellHeight);
        col = Math.round((lon - bounds[1]) / cellWidth);
      }
      
      // Calculate cell bounds
      const cellBounds: [number, number, number, number] = [
        lat - cellHeight / 2,
        lon - cellWidth / 2,
        lat + cellHeight / 2,
        lon + cellWidth / 2
      ];
      
      cells.push({
        id: `cell_${index}`,
        coordinate: coord,
        probability,
        confidence: confidence,
        row,
        col,
        bounds: cellBounds
      });
    });
    
    return cells;
  }
}

const GridPotentialLayer: React.FC<GridPotentialLayerProps> = ({
  map,
  gridData,
  mineralType,
  visible = true,
  onVisibilityChange,
  onCellClick,
  zoomLevel = 10,
  polygonBounds,
  showControlPanel = true,
  threshold = 0.1,
  opacity = 0.7,
  modulated = false
}) => {
  // Removed performance-critical render logs

  // Component state - local controls override props
  const showConfidence = modulated;
  const [renderMode, setRenderMode] = useState<'gradient' | 'hybrid' | 'discrete'>('gradient');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useAdaptiveColors, setUseAdaptiveColors] = useState(true);
  
  // Local state for interactive controls (defaults favour showing the full distribution)
  const [localThreshold, setLocalThreshold] = useState(threshold || 0.05);
  const [localOpacity, setLocalOpacity] = useState(opacity || 0.85);
  
  // Sync local state with props when they change
  useEffect(() => {
    if (threshold === undefined) {
      return;
    }
    setLocalThreshold((previous) => (previous !== threshold ? threshold : previous));
  }, [threshold]);
  
  useEffect(() => {
    if (opacity === undefined) {
      return;
    }
    setLocalOpacity((previous) => (previous !== opacity ? opacity : previous));
  }, [opacity]);
  
  // Layer references
  const gridLayerRef = useRef<VectorLayer<VectorSource> | ImageLayer<ImageSource> | null>(null);
  // Memoized grid processing
  const gridCells = useMemo(() => {
    if (!gridData) return [];
    
    try {
      setError(null);
      const cells = GridTransformService.transformToGridCells(gridData, polygonBounds);
      
      // Enhanced debugging for grid structure analysis
      return cells;
    } catch (err) {
      const errorMsg = `Failed to process grid data: ${err}`;
      setError(errorMsg);
      console.error(errorMsg);
      return [];
    }
  }, [gridData, polygonBounds]);
  
  const gridStructure = useMemo(() => {
    if (!gridData?.coordinates) return null;
    return GridTransformService.detectGridStructure(gridData.coordinates);
  }, [gridData?.coordinates]);
  
  // Update render mode based on zoom level
  useEffect(() => {
    if (zoomLevel < ZOOM_THRESHOLDS.SMOOTH_GRADIENT) {
      setRenderMode('gradient');
    } else if (zoomLevel < ZOOM_THRESHOLDS.DISCRETE_CELLS) {
      setRenderMode('hybrid');
    } else {
      setRenderMode('discrete');
    }
  }, [zoomLevel]);
  
  // Calculate adaptive percentile-based color stops for better visual discrimination
  const adaptiveColorStops = useMemo(() => {
    if (!gridCells.length) return null;
    
    const probabilities = gridCells.map(cell => cell.probability).sort((a, b) => a - b);
    const percentiles = [0.05, 0.25, 0.5, 0.75, 0.90, 0.95, 1.0];
    
    const adaptiveStops = percentiles.map(percentile => {
      const index = Math.max(0, Math.min(probabilities.length - 1, Math.floor(percentile * probabilities.length)));
      return probabilities[index];
    });
    
    return adaptiveStops;
  }, [gridCells]);

  // Color interpolation function with adaptive percentile mapping
  const interpolateColor = useCallback((value: number, mineral: string): [number, number, number, number] => {
    const scheme = MINERAL_COLOR_SCHEMES[mineral as keyof typeof MINERAL_COLOR_SCHEMES] || MINERAL_COLOR_SCHEMES.gold;
    let stops = scheme.stops;
    
    // Use adaptive stops if enabled and available for better visual discrimination
    if (useAdaptiveColors && adaptiveColorStops && adaptiveColorStops.length === 7) {
      // Map adaptive percentiles to color stops
      stops = [
        { value: adaptiveColorStops[0], color: [25, 25, 112, 0] },      // P5: Transparent navy
        { value: adaptiveColorStops[1], color: [70, 130, 180, 120] },   // P25: Steel blue
        { value: adaptiveColorStops[2], color: scheme.stops[2].color }, // P50: Mid-range color
        { value: adaptiveColorStops[3], color: scheme.stops[3].color }, // P75: Higher color
        { value: adaptiveColorStops[4], color: scheme.stops[4].color }, // P90: High color
        { value: adaptiveColorStops[5], color: scheme.stops[5].color }, // P95: Very high color
        { value: adaptiveColorStops[6], color: scheme.stops[6].color }  // P100: Maximum color
      ];
    }
    
    // Clamp value
    value = Math.max(0, Math.min(1, value));
    
    // Find surrounding stops
    let lowerStop = stops[0];
    let upperStop = stops[stops.length - 1];
    
    for (let i = 0; i < stops.length - 1; i++) {
      if (value >= stops[i].value && value <= stops[i + 1].value) {
        lowerStop = stops[i];
        upperStop = stops[i + 1];
        break;
      }
    }
    
    // Interpolate
    const range = upperStop.value - lowerStop.value;
    const factor = range > 0 ? (value - lowerStop.value) / range : 0;
    
    return [
      Math.round(lowerStop.color[0] + (upperStop.color[0] - lowerStop.color[0]) * factor),
      Math.round(lowerStop.color[1] + (upperStop.color[1] - lowerStop.color[1]) * factor),
      Math.round(lowerStop.color[2] + (upperStop.color[2] - lowerStop.color[2]) * factor),
      Math.round(lowerStop.color[3] + (upperStop.color[3] - lowerStop.color[3]) * factor)
    ];
  }, [adaptiveColorStops, useAdaptiveColors]);
  
  // Create and update grid layer
  useEffect(() => {
    // Quiet condition checking
    if (!map || !visible || !gridCells.length || !gridData?.metadata) {
      if (gridLayerRef.current) {
                map?.removeLayer(gridLayerRef.current);
        gridLayerRef.current = null;
      }
      return;
    }
    
    setIsLoading(true);
    
    try {
      const metadata = gridData.metadata;
      
      // Use VectorLayer approach for stable grid positioning
      const vectorSource = new VectorSource();
      
      // Create individual polygon features for each grid cell  
      gridCells.forEach((cell: GridCell, index: number) => {
        const probability = cell.probability;
        if (index === 0) {
                  }
        if (probability < localThreshold) return;
        
        const [lat, lon] = cell.coordinate;
        const cellWidth = metadata.cell_dimensions?.width_deg || 0.01;
        const cellHeight = metadata.cell_dimensions?.height_deg || 0.01;
        
        // Create polygon coordinates for the cell with proper spacing to prevent overlap
        // Reduce cell size slightly to create gaps between cells
        const gapFactor = 0.95; // 95% of original size to create small gaps
        const adjustedWidth = cellWidth * gapFactor;
        const adjustedHeight = cellHeight * gapFactor;
        
        const cellCoords = [
          [lon - adjustedWidth/2, lat - adjustedHeight/2],
          [lon + adjustedWidth/2, lat - adjustedHeight/2],
          [lon + adjustedWidth/2, lat + adjustedHeight/2],
          [lon - adjustedWidth/2, lat + adjustedHeight/2],
          [lon - adjustedWidth/2, lat - adjustedHeight/2]
        ].map(coord => fromLonLat([coord[0], coord[1]]));
        
        const cellFeature = new Feature({
          geometry: new Polygon([cellCoords]),
          probability: probability,
          cellId: cell.id
        });
        
        // Style the cell with the appropriate color
        const [r, g, b, a] = interpolateColor(probability, mineralType);
        const alpha = (a / 255) * localOpacity;
        
        cellFeature.setStyle(new Style({
          fill: new Fill({
            color: `rgba(${r}, ${g}, ${b}, ${alpha})`
          }),
          stroke: renderMode === 'discrete' ? new Stroke({
            color: `rgba(255, 255, 255, 0.1)`,
            width: 0.5
          }) : undefined
        }));
        
        vectorSource.addFeature(cellFeature);
      });
      
      const layer = new VectorLayer({
        source: vectorSource,
        opacity: 0.8
      });
      
      // Remove old layer
      if (gridLayerRef.current) {
                map.removeLayer(gridLayerRef.current);
      }
      
            map.addLayer(layer);
      gridLayerRef.current = layer;
      
      // Force layer to be visible and on top
      layer.setVisible(true);
      layer.setZIndex(1000); // High z-index to ensure it's on top
      
      } catch (err) {
      console.error('Failed to create grid layer:', err);
      setError(`Rendering error: ${err}`);
    } finally {
      setIsLoading(false);
    }
    
  }, [map, visible, gridCells, gridData?.metadata, renderMode, mineralType, interpolateColor, useAdaptiveColors, localThreshold, localOpacity]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (map && gridLayerRef.current) {
        map.removeLayer(gridLayerRef.current);
      }
    };
  }, [map]);
  
  if (!gridData) {
    return null;
  }
  
  const validation = GridTransformService.validateGridData(gridData);
  
  return (
    <>
      {showControlPanel && (
        <Paper sx={{ 
          p: 2, 
          mb: 2,
          backgroundColor: '#161B22',
          border: '1px solid #30363D',
          borderRadius: '4px'
        }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{
          color: 'var(--palantir-blue-600)',
          fontFamily: 'monospace',
          fontSize: '14px',
          fontWeight: 'bold'
        }}>
          GRID POTENTIAL MAP - {mineralType.toUpperCase()}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLoading && <CircularProgress size={16} />}
          
          {error && (
            <Tooltip title={error}>
              <WarningIcon sx={{ color: 'orange', fontSize: 18 }} />
            </Tooltip>
          )}
          
          <Tooltip title={visible ? "Hide Grid" : "Show Grid"}>
            <IconButton
              onClick={() => onVisibilityChange(!visible)}
              sx={{
                color: visible ? '#00D2FF' : 'var(--palantir-text-muted)',
                '&:hover': {
                  color: visible ? '#00B8E6' : 'var(--palantir-text-primary)'
                }
              }}
            >
              {visible ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* Validation alerts */}
      {!validation.isValid && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Grid data validation failed: {validation.errors.join(', ')}
        </Alert>
      )}
      
      {validation.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {validation.warnings.join(', ')}
        </Alert>
      )}
      
      {/* Grid info */}
      {gridStructure && (
        <Box sx={{ 
          mb: 2,
          p: 1.5,
          backgroundColor: '#0D1117',
          borderRadius: '4px',
          border: '1px solid #30363D'
        }}>
          <Typography sx={{ color: '#00D2FF', fontSize: '10px', fontFamily: 'monospace', mb: 1 }}>
            GRID STRUCTURE
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, fontSize: '11px', fontFamily: 'monospace' }}>
            <span style={{ color: 'var(--palantir-text-primary)' }}>
              Type: {gridStructure.gridType}
            </span>
            <span style={{ color: 'var(--palantir-text-primary)' }}>
              Size: {gridStructure.detectedRows}×{gridStructure.detectedCols}
            </span>
            <span style={{ color: 'var(--palantir-text-primary)' }}>
              Cells: {gridCells.length}
            </span>
          </Box>
        </Box>
      )}

      {/* Probability Distribution Statistics */}
      {gridCells.length > 0 && (
        <Box sx={{ 
          mb: 2,
          p: 1.5,
          backgroundColor: '#0D1117',
          borderRadius: '4px',
          border: '1px solid #30363D'
        }}>
          <Typography sx={{ color: '#FFB300', fontSize: '10px', fontFamily: 'monospace', mb: 1 }}>
            PROBABILITY DISTRIBUTION
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '10px', fontFamily: 'monospace' }}>
            {(() => {
              const probabilities = gridCells.map(cell => cell.probability);
              const min = Math.min(...probabilities);
              const max = Math.max(...probabilities);
              const mean = probabilities.reduce((a, b) => a + b, 0) / probabilities.length;
              const sorted = [...probabilities].sort((a, b) => a - b);
              const p25 = sorted[Math.floor(0.25 * sorted.length)];
              const p50 = sorted[Math.floor(0.50 * sorted.length)];
              const p75 = sorted[Math.floor(0.75 * sorted.length)];
              
              return (
                <>
                  <span style={{ color: 'var(--palantir-text-primary)' }}>
                    Range: {(min * 100).toFixed(1)}% - {(max * 100).toFixed(1)}%
                  </span>
                  <span style={{ color: 'var(--palantir-text-primary)' }}>
                    Mean: {(mean * 100).toFixed(1)}%
                  </span>
                  <span style={{ color: 'var(--palantir-text-primary)' }}>
                    Q25: {(p25 * 100).toFixed(1)}%
                  </span>
                  <span style={{ color: 'var(--palantir-text-primary)' }}>
                    Median: {(p50 * 100).toFixed(1)}%
                  </span>
                  <span style={{ color: 'var(--palantir-text-primary)' }}>
                    Q75: {(p75 * 100).toFixed(1)}%
                  </span>
                  <span style={{ color: useAdaptiveColors ? '#4CAF50' : '#FFC107' }}>
                    {useAdaptiveColors ? 'Adaptive Mapping' : 'Fixed Mapping'}
                  </span>
                </>
              );
            })()}
          </Box>
        </Box>
      )}
      
      {/* Controls */}
      {visible && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Threshold slider */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: '11px',
              mb: 1
            }}>
              PROBABILITY THRESHOLD: {(localThreshold * 100).toFixed(0)}%
            </Typography>
            <Slider
              value={localThreshold}
              onChange={(event, newValue) => setLocalThreshold(newValue as number)}
              min={0.0}
              max={0.9}
              step={0.05}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              sx={{
                color: 'var(--palantir-blue-600)',
                '& .MuiSlider-thumb': {
                  backgroundColor: 'var(--palantir-blue-600)'
                }
              }}
            />
          </Box>
          
          {/* Opacity slider */}
          <Box>
            <Typography sx={{
              color: '#00D2FF',
              fontFamily: 'monospace',
              fontSize: '11px',
              mb: 1
            }}>
              LAYER OPACITY: {Math.round(localOpacity * 100)}%
            </Typography>
            <Slider
              value={localOpacity}
              onChange={(event, newValue) => setLocalOpacity(newValue as number)}
              min={0.1}
              max={1.0}
              step={0.1}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
              sx={{
                color: '#00D2FF',
                '& .MuiSlider-thumb': {
                  backgroundColor: '#00D2FF'
                }
              }}
            />
          </Box>
          
          {/* Toggle switches */}
          <FormControlLabel
            control={
              <Switch
                checked={useAdaptiveColors}
                onChange={(e) => setUseAdaptiveColors(e.target.checked)}
                sx={{
                  '& .MuiSwitch-thumb': {
                    backgroundColor: useAdaptiveColors ? '#4CAF50' : '#757575'
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: useAdaptiveColors ? '#81C784' : '#BDBDBD'
                  }
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: '12px', fontFamily: 'monospace', color: useAdaptiveColors ? '#4CAF50' : '#FFC107' }}>
                Adaptive Color Mapping
              </Typography>
            }
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={showConfidence}
                onChange={() => {}} // Disabled - controlled from sidebar
                disabled
              />
            }
            label={
              <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                Modulate by Confidence
              </Typography>
            }
          />
        </Box>
      )}
      </Paper>
      )}
    </>
  );
};

export default React.memo(GridPotentialLayer);
